import { useEffect, useState } from 'react';
import { getPlanningAssessment, type PlanningAssessment } from '../api/planningAssessments';
import { getPlanningDocumentDownloadUrl } from '../api/planningDocuments';
import { errMessage, renderMarkdown } from '../pageUtils';
import { DescriptionConversation, conversationFor, type ConversationCheck } from './DescriptionConversation';

// A case's earlier finished checks, newest first, so someone revising a proposal can see what each version was and
// what it got. Each check's full analysis and report load only when it is opened.

type Summary = Pick<PlanningAssessment, 'id' | 'score' | 'riskBand' | 'proposalDescription' | 'assessedAt' | 'createdAt'>;

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function EarlierCheck({ check, all }: { check: Summary; all: ConversationCheck[] }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState<PlanningAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || full) return;
    let cancelled = false;
    setLoading(true); setError(null);
    getPlanningAssessment(check.id)
      .then(a => { if (!cancelled) setFull(a ?? null); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, check.id, full]);

  async function openPdf() {
    if (!full?.pdf) return;
    setPdfBusy(true); setPdfError(null);
    // Opened synchronously so mobile browsers don't treat the later navigation as an unwanted popup.
    const win = window.open('', '_blank');
    try {
      const url = await getPlanningDocumentDownloadUrl(full.pdf.id);
      if (win) win.location.href = url; else window.location.assign(url);
    } catch (e) {
      win?.close();
      setPdfError(errMessage(e, 'Could not open the report.'));
    } finally {
      setPdfBusy(false);
    }
  }

  const when = fmtWhen(check.assessedAt ?? check.createdAt);
  return (
    <details className="sx-details" onToggle={e => setOpen(e.currentTarget.open)}>
      <summary>
        {when}
        <span className="sx-earlier-band">{check.riskBand ?? 'Assessed'}{check.score != null ? ` · ${check.score} / 100` : ''}</span>
      </summary>
      <div className="sx-earlier-body">
        <div>
          <span className="sx-label">What you described</span>
          {check.proposalDescription
            ? <DescriptionConversation messages={conversationFor(all, check.id)} />
            : <p className="sx-note">Not recorded: this check was run before descriptions were kept with each check.</p>}
        </div>
        {error ? (
          <div className="sx-err">Couldn't load this check: {error}</div>
        ) : loading || !full ? (
          <span className="skel" style={{ width: '100%', height: 40 }} />
        ) : (
          <>
            {full.pdf && <button type="button" className="sx-cta sx-cta-2" onClick={() => void openPdf()} disabled={pdfBusy}>{pdfBusy ? 'Opening…' : 'Download this report (PDF)'}</button>}
            {pdfError && <div className="sx-err">{pdfError}</div>}
            {full.analysis && (
              <details className="sx-details">
                <summary>Read this analysis</summary>
                <div className="sx-prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(full.analysis) }} />
              </details>
            )}
          </>
        )}
      </div>
    </details>
  );
}

export function EarlierChecks({ checks, all }: { checks: Summary[]; all: ConversationCheck[] }) {
  if (!checks.length) return null;
  return (
    <section className="sx-stack" aria-labelledby="sx-earlier-h">
      <h2 className="sx-h2" id="sx-earlier-h">Earlier checks</h2>
      <p className="sx-note">Each earlier version of your proposal and what it got, newest first.</p>
      {checks.map(c => <EarlierCheck key={c.id} check={c} all={all} />)}
    </section>
  );
}
